import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/api/client';

export function ResetMfaPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/forgot-mfa');
    }
  }, [token, navigate]);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/auth/mfa-reset/consume', { token });
      if (!data.data) throw new Error(data.errors?.[0]?.message ?? 'Reset failed');
      return data;
    },
    onSuccess: () => setSuccess(true),
    onError: () => toast.error('This reset link has expired or is invalid. Request a new one.'),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,159,253,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,159,253,1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-12 w-auto" />
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-text-primary font-display">VeriGRC</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Unified Cyber GRC Platform</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-accent-emerald" />
              <p className="text-base font-medium text-text-primary">MFA reset complete</p>
              <p className="text-sm text-text-secondary">
                Your authenticator has been cleared. All existing sessions have been signed out.
                Please re-enroll on next sign-in.
              </p>
              <Link to="/login" className="mt-2 text-sm text-accent-cyan hover:underline">
                Sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-text-primary">Reset your MFA</h2>
                <p className="mt-0.5 text-sm text-text-secondary">
                  Click the button below to clear your current authenticator. You will be prompted
                  to re-enroll on your next sign-in.
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  type="button"
                  className="w-full"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate()}
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting…
                    </>
                  ) : (
                    'Reset my MFA'
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to="/forgot-mfa"
                    className="text-sm text-text-secondary hover:text-text-primary"
                  >
                    Request a new link
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
